import PropTypes from "prop-types";
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Typography,
} from "@mui/material";

import ComponentAbstract from "components/ComponentAbstract";
import ImageError from "components/ImageError";
import _helper from "actions/_helper";
import _request from "actions/_request";
import i18n from "i18";
import ArrowCircleLeftOutlinedIcon from "@mui/icons-material/ArrowCircleLeftOutlined";

import "./style.css";
import LinearDeterminate from "../LinearDeterminate";
import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined";
import React from "react";
import { ReactComponent as NoData } from "../../images/No files.svg";

export default class FilesManager extends ComponentAbstract {
  static propTypes = {
    size: PropTypes.oneOf(["small", "big"]),
    files: PropTypes.array,
    accept: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    refresh: PropTypes.func,
    onSelectionChange: PropTypes.func,
  };

  static defaultProps = {
    size: "big",
    files: [],
    accept: [],
    refresh: null,
    onSelectionChange: null,
  };

  constructor(props) {
    super(props);

    this.state = {
      openedMenu: null,
      foldersPath: [],
      selected: [],
      editFolderDialogProps: null,
      totalUploadTime: 0,
    };
  }

  componentDidMount() {
    _helper.setFileSystem(this.domNode, this.upload.bind(this));
  }

  toggleSelection(id) {
    const selected = this.toggleStateArray("selected", id);

    this.props.onSelectionChange(selected);
  }

  formLink(id, linkToThumb) {
    return _request.files.formLink(id, linkToThumb);
  }

  getLastPathFolder() {
    const { foldersPath } = this.state;
    return foldersPath.at(-1);
  }

  uploadDialog() {
    let { accept } = this.props;
    if (accept?.unshift) accept = accept.join(",");

    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("multiple", true);

    if (accept) input.setAttribute("accept", accept);

    input.addEventListener("change", ({ target }) => {
      input.remove();
      this.upload(target.files);
    });

    input.click();
  }

  upload(filesOrig) {
    if (filesOrig.length > 10)
      return _helper.alertError(i18n.t("fileManager.uploadLimit"));

    let { accept } = this.props;
    const folder_id = this.getLastPathFolder()?.id || null;

    const files = Array.from(filesOrig).filter(
      (it) => !accept || (it.type && accept.includes(it.type))
    );

    const startTime = new Date();

    this.setState({ isLoading: true });

    _request.files.loadingFile(async () => {
      await _request.files.upload(folder_id, files);

      const endTime = new Date();

      const uploadTimeInMillis = endTime - startTime;

      const uploadTimeInSeconds = uploadTimeInMillis / 1000;

      this.setState((prevState) => ({
        totalUploadTime: prevState.totalUploadTime + uploadTimeInSeconds,
        isLoading: false,
      }));

      if (this.props.refresh) {
        this.props.refresh();
      }
    });
  }

  previewFile({ id, file_type }) {
    const src = this.formLink(id, false);

    _helper.showPreview({ src, type: file_type });
  }

  getEditFolderContent(tProps, onClick) {
    return this.insertGlobalInput(
      {
        inputRef: (el) => (this.newFolderName = el),
        placeholder: i18n.t("fileManager.folderName"),
        autoFocus: true,
        size: "small",
        InputProps: {
          sx: {
            color: "var(--text-color)",
            backgroundColor: "var(--background)",
            border: "1px solid var(--text-color)",
            borderRadius: "7px",
            minWidth: "400px",
          },
        },
        ...tProps,
      },
      {
        onEnter: () => {
          this.changeState("editFolderDialogProps", null);
          onClick();
        },
      }
    );
  }

  openEditFolderDialog(title, label, onClick, tProps = {}) {
    this.openEditDialog("editFolderDialogProps", {
      title,
      label,
      onClick,
      text: this.getEditFolderContent(tProps, onClick),
    });
  }

  newFolder() {
    this.openEditFolderDialog(
      "fileManager.createFolder",
      "create",
      this.addFolder.bind(this)
    );
  }

  addFolder() {
    const file_name = this.newFolderName.value;
    if (!file_name) return;

    const folder_id = this.getLastPathFolder()?.id || null;

    _request.files.loading(async () => {
      return await _request.files.add({
        file_name,
        folder_id,
        file_size: 0,
        file_type: "folder",
        status: "ready",
      });
    }, this.props.refresh && this.props.refresh());
  }

  updateFolder(id) {
    const file_name = this.newFolderName.value;
    if (!file_name) return;

    _request.files.loading(async () => {
      return await _request.files.update(id, { file_name });
    }, this.props.refresh && this.props.refresh());
  }

  editFolder(info) {
    const defaultValue = info?.file_name || "";
    this.openEditFolderDialog(
      "fileManager.editFolder",
      "save",
      this.updateFolder.bind(this, info?.id),
      { defaultValue }
    );
  }

  deleteFile({ id }) {
    _helper.confirm(
      "dialog.fileRemove",
      this.deleteFileConfirmed.bind(this, id)
    );
  }

  deleteFileConfirmed(id) {
    _request.files.loading(async () => {
      return await _request.files.delete(id);
    }, this.props.refresh && this.props.refresh());
  }

  setMenuOpened(openedMenu, cb) {
    this.setState({ openedMenu }, cb);
  }

  openFolder(info) {
    const { foldersPath } = this.state;

    foldersPath.push(info);

    this.setState({ foldersPath });
  }

  backPath(index) {
    let { foldersPath } = this.state;

    if (index < 0) foldersPath.pop();
    else foldersPath = foldersPath.slice(0, -index);

    this.setState({ foldersPath });
  }

  insertFileActionsMenu() {
    const { openedMenu } = this.state;
    if (!openedMenu) return <></>;

    const { vInfo, vIsFolder } = openedMenu || {};

    return (
      <Menu
        open={true}
        anchorEl={openedMenu}
        onClose={this.setMenuOpened.bind(this, null, null)}
        slotProps={{
          paper: {
            sx: { minWidth: "100px" },
          },
        }}>
        {!vIsFolder && (
          <MenuItem
            onClick={this.setMenuOpened.bind(
              this,
              null,
              this.previewFile.bind(this, vInfo)
            )}
            className="font f14">
            {i18n.t("view")}
          </MenuItem>
        )}
        {vIsFolder && (
          <MenuItem
            onClick={this.setMenuOpened.bind(this, null, this.editFolder.bind(this, vInfo))}
            className="font f14">
            {i18n.t("edit")}
          </MenuItem>
        )}
        <MenuItem
          onClick={this.setMenuOpened.bind(
            this,
            null,
            this.deleteFile.bind(this, vInfo)
          )}
          className="font f14">
          {i18n.t("delete")}
        </MenuItem>
      </Menu>
    );
  }

  insertDotButton(info, isFolder) {
    return (
      <IconButton
        className="dotsIcon clear"
        onClick={(e) => {
          e.stopPropagation();

          const { currentTarget } = e;

          currentTarget.vInfo = info;
          currentTarget.vIsFolder = isFolder;

          this.setMenuOpened(currentTarget, null);
        }}>
        <Box className="dot radR"></Box>
        <Box className="dot radR"></Box>
        <Box className="dot radR"></Box>
      </IconButton>
    );
  }
  insertNonEmptyFolder(size, key, cl, info, count) {
    const { files, width, height } = this.props;
    const imageFiles = files.filter((file) => file.folder_id === info.id);

    return (
      <Paper
        key={key}
        className="box relative"
        sx={{ width: `${width}px`, height: `${height}px` }}
        onClick={this.openFolder.bind(this, info)}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <Grid container spacing={2} className="imageNone">
              {imageFiles.slice(0, 4).map((file, index) => (
                <Grid
                  item
                  xs={imageFiles.length === 1 ? 12 : 6}
                  key={index}
                  style={
                    imageFiles.length === 1
                      ? { width: "84px", height: "40px" }
                      : {}
                  }>
                  <ImageError
                    src={this.formLink(file.id)}
                    alt={`image-${index}`}
                    className="icon"
                    width={84}
                    height={40}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
        <Box className="flex flex-col items-start mt-4 absolute bottom-1">
          {count < 3 && (
            <Typography
              variant="subtitle1"
              className="folderName"
              align="center">
              {info.file_name}
            </Typography>
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}>
            <Box className="cntIcon" />
            <Typography variant="body2" className="fileCount">
              {count} Files
            </Typography>
          </Box>
        </Box>
        <Box className="panel flex justify-end startV asss">
          {this.insertDotButton(info, true)}
        </Box>
      </Paper>
    );
  }

  insertFile(key, info) {
    const { size, onSelectionChange, files } = this.props;
    const { id, file_type } = info;
    const fileName = info.file_name.split("/").pop();
    const { openedMenu } = this.state;
    const isFolder = file_type === "folder";
    const countFilesInFolder = (folderId) => {
      return files.filter((file) => file.folder_id === folderId).length;
    };
    let count = countFilesInFolder(info.id) || 0;

    if (file_type === "image") {
      count = info.folder_id !== null;
    }
    const cl = openedMenu && id === openedMenu?.vInfo.id ? "active" : "";

    if (isFolder) {
      return this.insertNonEmptyFolder(size, key, cl, info, count);
    }

    const item = files.find((file) => file.id === id);

    const selCl = onSelectionChange?.apply ? "hand" : "";
    const cb = onSelectionChange ? this.toggleSelection.bind(this, id) : null;
    const src = this.formLink(id);

    const isOnPostPage = window.location.href.endsWith("/app/post");
    const previewHandler = isOnPostPage
      ? null
      : this.previewFile.bind(this, info);

    return (
      <Box
        key={key}
        className={`file pos rel flex centerV centerH bgWhite o3 ${size} ${cl} ${selCl}`}
        onClick={cb}>
        {item.status === "pending" ? (
          <Box position="relative" width="176px" height="176px">
            <Skeleton
              variant="rectangular"
              animation="wave"
              width="100%"
              height="100%"
              sx={{ borderRadius: "8px" }}
            />
            <CircularProgress
              size={48}
              sx={{
                position: "absolute",
                top: "35%",
                left: "35%",
                transform: "translate(-50%, -50%)",
              }}
            />
          </Box>
        ) : (
          <ImageError
            src={src}
            alt={key}
            className="img scale cursor-pointer"
            onClick={previewHandler}
          />
        )}
        <Typography className="fileInfo">{fileName}</Typography>
        <Box className="panel flex justify-end startV">
          {this.insertDotButton(info, false)}
        </Box>
      </Box>
    );
  }

  retrievePath(paths) {
    const last = paths.at(-1);
    const l = paths.length;

    return (
      <Box className="path flex col centerV gap8">
        <IconButton
          sx={{ color: "#42C0C7" }}
          className="clear"
          onClick={this.backPath.bind(this, -1)}>
          <ArrowCircleLeftOutlinedIcon className="font f28" color="inherit" />
        </IconButton>
        <Box className="names flex wrap gap4">
          {paths.map(({ id, file_name }, i) => {
            const cl = id === last.id ? "" : " col black03";

            return (
              <Box
                key={i}
                className={"name" + cl}
                onClick={this.backPath.bind(this, l - i - 1)}>
                <Typography sx={{ color: "#42C0C7" }}>{file_name}</Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  retrieveFiles(arr, selected) {
    const folder_id = this.getLastPathFolder()?.id || null;
    const { padding } = this.props;

    const items = arr
      .filter(
        (it) =>
          (it.folder_id || null) === folder_id &&
          (!selected.length || !selected.includes(it.id))
      )
      .sort((it) => (it.file_type === "folder" ? -1 : 1));

    if (items.length < 1) return this.insertEmpty();

    const { totalUploadTime } = this.state;

    return (
      <Box className="content flex1 flex wrap" sx={{ padding: padding }}>
        {items.map((it, i) => this.insertFile(i, it))}
        {this.state.isLoading ? (
          <Box
            sx={{
              width: "244px",
              height: "181px",
              backgroundColor: "#ffffff",
              borderTopLeftRadius: "15px",
              borderTopRightRadius: "15px",
              opacity: 0.5,
              position: "relative",
              boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
            }}>
            <BrokenImageOutlinedIcon
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                fontSize: "90px",
              }}
            />
            <LinearDeterminate uploadTime={totalUploadTime} />
          </Box>
        ) : null}
      </Box>
    );
  }

  insertEmpty() {
    const isOnPostPage = window.location.href.endsWith("/app/post");

    return (
      <Box className="flex1 flex column centerV centerH gap16">
        <Box
          className="flex flex-col text-center items-center justify-center"
          width={isOnPostPage ? 250 : "100%"}
          height={isOnPostPage ? 250 : "100%"}>
          <NoData />
          <Typography
            variant="body1"
            className="font w300"
            sx={{ color: "var(--text-color)" }}>
            {i18n.t("oopsEmpty")}
          </Typography>
          <Typography
            variant="body2"
            className="font w300"
            sx={{ color: "var(--text-color)" }}>
            {i18n.t("fileManager.emptyText")}
          </Typography>
          {!isOnPostPage && (
            <Box
              className="btnDark mt-3"
              onClick={() => this.uploadDialog()}
              style={{ borderRadius: "23px" }}>
              {i18n.t("fileManager.uploadFile")}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  render() {
    const { files } = this.props;
    const { foldersPath, editFolderDialogProps, selected } = this.state;

    return (
      <Box
        ref={(el) => (this.domNode = el)}
        className="filesManager flex1 flex column gap8 dragAble">
        {foldersPath.length > 0 && this.retrievePath(foldersPath)}
        {this.retrieveFiles(files, selected)}
        {this.insertFileActionsMenu()}
        {!!editFolderDialogProps &&
          this.insertDialog(
            editFolderDialogProps,
            this.changeState.bind(this, "editFolderDialogProps", null)
          )}
      </Box>
    );
  }
}
